import { computed, Injectable, signal } from '@angular/core';
import { TokenService } from '@foxera/shared/services/token.service';
import { PERMISSIONS, Permission } from '@foxera/shared/constants/permissions.constants';

/**
 * Permission Service
 * 
 * Centralized, reactive service for permission-based access control.
 * Uses computed signals for efficient, reactive permission checks.
 * 
 * Permissions are loaded from the GetProfile API after tenant selection.
 * 
 * @example
 * ```typescript
 * // In component
 * constructor(private permissionService: PermissionService) {}
 * 
 * // In template - reactive computed signals
 * @if (permissionService.canAccessReminders()) {
 *   <button>Reminders</button>
 * }
 * 
 * // In component logic
 * if (this.permissionService.canAccess(PERMISSIONS.ACCESS_DASHBOARD)) {
 *   // Allow access
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  // Internal signal for user permissions (Set for O(1) lookup performance)
  private readonly userPermissionsSet = signal<Set<string>>(new Set());

  constructor(private readonly tokenService: TokenService) {
    this.initializePermissions();
    this.subscribeToUserInfoChanges();
  }

  /**
   * Initialize permissions from stored user info
   */
  private initializePermissions(): void {
    const permissions = this.tokenService.getUserPermissions();
    this.userPermissionsSet.set(new Set(permissions));
  }

  /**
   * Subscribe to user info changes to keep permissions synchronized
   */
  private subscribeToUserInfoChanges(): void {
    this.tokenService.userInfo$.subscribe(() => {
      this.initializePermissions();
    });
  }

  /**
   * Core permission check method (O(1) lookup using Set)
   */
  private hasPermissionInternal(permission: Permission | string): boolean {
    return this.userPermissionsSet().has(permission);
  }

  /**
   * Generic permission check method
   * @param permission - The permission to check
   * @returns True if user has the permission
   * 
   * @example
   * ```typescript
   * if (this.permissionService.canAccess(PERMISSIONS.ACCESS_DASHBOARD)) {
   *   // Allow access
   * }
   * ```
   */
  canAccess(permission: Permission | string): boolean {
    return this.hasPermissionInternal(permission);
  }

  /**
   * Alias for canAccess - checks if user has permission
   */
  hasPermission(permission: Permission | string): boolean {
    return this.canAccess(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns True if user has at least one permission
   * 
   * @example
   * ```typescript
   * if (this.permissionService.canAccessAny([
   *   PERMISSIONS.ACCESS_DASHBOARD,
   *   PERMISSIONS.ACCESS_SETTINGS
   * ])) {
   *   // User has at least one permission
   * }
   * ```
   */
  canAccessAny(permissions: readonly (Permission | string)[]): boolean {
    const permissionsSet = this.userPermissionsSet();
    return permissions.some(permission => permissionsSet.has(permission));
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns True if user has all permissions
   * 
   * @example
   * ```typescript
   * if (this.permissionService.canAccessAll([
   *   PERMISSIONS.ADD_EDIT_CLIENT,
   *   PERMISSIONS.ACCESS_CLIENT_FILES
   * ])) {
   *   // User has all required permissions
   * }
   * ```
   */
  canAccessAll(permissions: readonly (Permission | string)[]): boolean {
    const permissionsSet = this.userPermissionsSet();
    return permissions.every(permission => permissionsSet.has(permission));
  }

  /**
   * Get all user permissions as an array
   * @returns Immutable copy of user permissions
   */
  getUserPermissions(): readonly string[] {
    return Array.from(this.userPermissionsSet());
  }

  /**
   * Check if user has any permissions
   * @returns True if user has at least one permission
   */
  hasAnyPermission(): boolean {
    return this.userPermissionsSet().size > 0;
  }

  // ============================================================================
  // Reactive Computed Signals for Each Permission
  // These are efficient, cached, and reactive - perfect for template binding
  // ============================================================================

  /** Reactive signal: Can view other schedules */
  readonly canViewOtherSchedules = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.VIEW_OTHER_SCHEDULES)
  );

  /** Reactive signal: Can modify appointments */
  readonly canModifyAppointments = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.MODIFY_APPOINTMENTS)
  );

  /** Reactive signal: Can access reminders */
  readonly canAccessReminders = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_REMINDERS)
  );

  /** Reactive signal: Can view client balance from appointment */
  readonly canViewClientBalanceFromAppointment = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.VIEW_CLIENT_BALANCE_FROM_APPOINTMENT)
  );

  /** Reactive signal: Can access invoices */
  readonly canAccessInvoices = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_INVOICES)
  );

  /** Reactive signal: Can access payments */
  readonly canAccessPayments = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_PAYMENTS)
  );

  /** Reactive signal: Can access dashboard */
  readonly canAccessDashboard = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_DASHBOARD)
  );

  /** Reactive signal: Can add/edit clients */
  readonly canAddEditClient = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ADD_EDIT_CLIENT)
  );

  /** Reactive signal: Can access client files */
  readonly canAccessClientFiles = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_CLIENT_FILES)
  );

  /** Reactive signal: Can access client appointments */
  readonly canAccessClientAppointments = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_CLIENT_APPOINTMENTS)
  );

  /** Reactive signal: Can access client memberships */
  readonly canAccessClientMemberships = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_CLIENT_MEMBERSHIPS)
  );

  /** Reactive signal: Can view client workout plans */
  readonly canViewClientWorkoutPlans = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.VIEW_CLIENT_WORKOUT_PLANS)
  );

  /** Reactive signal: Can add workout plan */
  readonly canAddWorkoutPlan = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ADD_WORKOUT_PLAN)
  );

  /** Reactive signal: Can edit workout plan */
  readonly canEditWorkoutPlan = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.EDIT_WORKOUT_PLAN)
  );

  /** Reactive signal: Can access catalog */
  readonly canAccessCatalog = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_CATALOG)
  );

  /** Reactive signal: Can access employees */
  readonly canAccessEmployees = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_EMPLOYEES)
  );

  /** Reactive signal: Can access settings */
  readonly canAccessSettings = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_SETTINGS)
  );

  /** Reactive signal: Can access live workout */
  readonly canAccessLiveWorkout = computed(() =>
    this.hasPermissionInternal(PERMISSIONS.ACCESS_LIVE_WORKOUT)
  );
}
